import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { systemDocumentByEntryId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

/**
 * Load a system-document by Rolodex entry id (develop navigation identity).
 * Requires an active or preview pipeline-config scope.
 */
export function useSystemDocumentByEntryId(
  entryId: string | undefined,
  siteId: string
): {
  systemDocumentEntry: SystemDocumentEntry | undefined
  systemDocumentEntryId: string | undefined
  cantileverDocumentEntryId: string | undefined
  filename: string | undefined
  bqDocumentId: string | undefined
  isLoading: boolean
} {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const documentLoadOptions = useMemo(() => {
    if (isPipelineConfigScopeLoading || !entryId) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({ site: siteId })
    if (!scopedTags) return undefined

    return systemDocumentByEntryId(entryId, scopedTags)
  }, [
    entryId,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteId,
  ])

  const { data: selectedDocument, isLoading } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      documentLoadOptions
    )

  const systemDocumentEntry = selectedDocument?.[0]
  const systemDocumentEntryId = systemDocumentEntry?.entry.id
  const cantileverDocumentEntryId = systemDocumentEntry
    ? getCantileverDocumentEntryId(systemDocumentEntry)
    : undefined
  const filename = systemDocumentEntry?.entry.data.filename
  const bqDocumentId = systemDocumentEntry
    ? getSystemDocumentBqId(systemDocumentEntry)
    : undefined

  return {
    systemDocumentEntry,
    systemDocumentEntryId,
    cantileverDocumentEntryId,
    filename,
    bqDocumentId,
    isLoading,
  }
}
